import React, { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import Button from './components/Button';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidMount(): void {
    const sentryKey = import.meta.env.VITE_SENTRY_DSN;

    if (!sentryKey) {
      console.error('Sentry DSN not provided. Error tracking disabled.');
      return;
    }

    Sentry.init({
      dsn: `${sentryKey}`,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 1.0,
      //   tracesSampleRate: 0,
      //   beforeSend() {
      //     return null;
      //   },
    });
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo });

    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen flex-col items-center justify-center bg-gray-100 text-gray-800">
          <div className="max-w-md rounded-lg bg-white p-6 text-center shadow-lg">
            <h1 className="mb-4 text-2xl font-bold text-red-500">Something went wrong</h1>
            <p className="mb-4 text-gray-600">We encountered an unexpected error. Please try again later.</p>
            {this.state.error && (
              <details className="rounded border border-gray-200 bg-gray-50 p-4 text-left">
                <summary className="cursor-pointer font-semibold text-gray-700">Error details</summary>
                <div className="mt-2 whitespace-pre-wrap text-sm text-gray-600">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack && (
                    <pre className="mt-2">{this.state.errorInfo.componentStack}</pre>
                  )}
                </div>
              </details>
            )}

            <Button
              onClick={() => window.location.reload()}
              className="mt-4 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2">
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
